import React from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import MuiBadge, { BadgeProps as MuiBadgeProps } from '@material-ui/core/Badge';
import classnames from 'classnames';

import { Typography } from './Typography';

const MAX_COUNTER = 99;

const styles = ({
  custom: { fontSizes },
  spacing: { unit },
  palette: {
    common: { white },
  },
}: Theme) =>
  createStyles({
    badge: {
      color: white,
      // WORKAROUND for lnum on Firefox
      // https://app.clickup.com/t/7am0vh
      fontVariantNumeric: 'lining-nums',
      fontFeatureSettings: 'lnum',
    },
    rootStandardRight: {},
    rootStandardRightCentered: {
      width: '100%',
      paddingLeft: unit / 2,
    },
    badgeStandardRight: {
      top: '50%',
      minWidth: unit * 3,
      height: unit * 2 + 1,
    },
    badgeStandardRightCentered: {
      right: (unit * 3) / 2 + unit,
    },
    rootDotRight: {},
    rootDotRightCentered: {
      marginLeft: -1 * unit,
    },
    badgeDotRight: {
      top: '50%',
      height: unit,
      width: unit,
    },
    badgeDotRightCentered: {
      right: -1 * unit,
    },
    badgeSizeSmall: {
      minWidth: 14,
      width: 14,
      height: 14,
      fontSize: fontSizes.tiny,
      // TODO Use another prop to update position? eg `compact=true` or `variant="compact"`
      top: 10,
      right: 10,
    },
  });

export interface BadgeProps extends Omit<MuiBadgeProps, 'classes' | 'variant'>, WithStyles<typeof styles> {
  variant?: MuiBadgeProps['variant'] | 'standard-right' | 'dot-right';
  size?: 'default' | 'small';
  centerContent?: boolean;
  badgeContentAsDot?: boolean;
}

const Badge: React.FC<BadgeProps> = ({
  classes,
  className,
  variant: initialVariant,
  size = 'default',
  badgeContent: initialBadgeContent,
  centerContent,
  badgeContentAsDot,
  ...rest
}) => {
  let variant = initialVariant;
  if (initialVariant === 'dot-right') {
    variant = 'dot';
  } else if (initialVariant === 'standard-right') {
    variant = 'standard';
  }
  const isBadgeVisible = rest.showZero || !!initialBadgeContent;
  const standardRight = isBadgeVisible && initialVariant === 'standard-right';
  const standardRightCentered = standardRight && centerContent;
  const dotRight = isBadgeVisible && initialVariant === 'dot-right';
  const dotRightCentered = dotRight && centerContent;

  let badgeContent = initialBadgeContent;
  if (Number(badgeContent) > MAX_COUNTER) badgeContent = <Typography color="inherit">+99</Typography>;
  if (isBadgeVisible && badgeContentAsDot) {
    badgeContent = <Typography color="inherit">●</Typography>;
  }

  return (
    <MuiBadge
      classes={{
        root: classnames({
          [classes.rootStandardRight]: standardRight,
          [classes.rootStandardRightCentered]: standardRightCentered,
          [classes.rootDotRight]: dotRight,
          [classes.rootDotRightCentered]: dotRightCentered,
        }),
        badge: classnames(
          classes.badge,
          {
            [classes.badgeStandardRight]: standardRight,
            [classes.badgeStandardRightCentered]: standardRightCentered,
            [classes.badgeDotRight]: dotRight,
            [classes.badgeDotRightCentered]: dotRightCentered,
            [classes.badgeSizeSmall]: size === 'small',
          },
          className,
        ),
      }}
      variant={variant as MuiBadgeProps['variant']}
      badgeContent={badgeContent}
      {...rest}
    />
  );
};

export default withStyles(styles, { name: 'LumaBadge' })(Badge);
